{
  "shadow_review": {
    "title": "Shadow Review",
    "subtitle": "Human-in-the-loop (ADR-0032 Phase 3). Resolve ambiguous verdicts and feed high-weight ground truth into the training set. Confirming proposes a label; a second analyst approves (4-eyes).",
    "loading": "Loading…",
    "admin_only": "admin only",
    "refresh": "Refresh",
    "tab": {
      "disagreement": "Disagreement",
      "single_judge": "Single-judge",
      "trusted": "Trusted candidates",
      "approvals": "Approvals"
    },
    "help": {
      "disagreement": "Events where the LSTM↔GNN consensus diverges — the human resolves the ambiguity.",
      "single_judge": "One ML judge scores high while the other (measured) scores low — candidate new patterns the consensus discounts.",
      "trusted": "Sources repeatedly suppressed (≥ 3× over 30 d). Promote = permanent whitelist (ADR-0008).",
      "approvals": "4-eyes queue — a second analyst (≠ proposer) promotes a proposed label into human_labels."
    },
    "empty": {
      "events": "No events in this queue.",
      "trusted": "No promotion candidates.",
      "approvals": "No pending approvals."
    },
    "pick_family": "family…",
    "reason_ph": "reason (≥ 5 chars, audit trail)",
    "confirm": "Confirm",
    "confirm_benign": "Confirm benign",
    "verdict": {
      "attack": "Attack",
      "benign": "Benign"
    },
    "bulk": {
      "selected": "{{n}} selected",
      "select_all": "Select all ({{n}})",
      "select_one": "Select this event",
      "confirm": "Label {{n}} event(s)",
      "clear": "Clear selection",
      "too_many": "Batch capped at {{max}} events — narrow the selection.",
      "already_pending": "{{n}} event(s) skipped: a proposal is already awaiting approval."
    },
    "family_new": {
      "open": "Propose a family missing from the catalogue",
      "slug_ph": "slug (e.g. Ransomware)",
      "name_ph": "display name",
      "rationale_ph": "rationale (≥ 5 chars) — why this family is missing",
      "create": "Create",
      "cancel": "Cancel",
      "slug_hint": "Letters, digits and “_” only, starting with a letter (2–64).",
      "duplicate": "Family “{{slug}}” already exists — select it, or ask the platform to merge.",
      "catalogue_error": "Attack family catalogue unavailable ({{msg}}) — labelling is blocked.",
      "exists_refresh_failed": "Family “{{slug}}” already exists, but the catalogue could not be refreshed — reload the page to select it."
    },
    "skip": "Skip",
    "promote": "Promote",
    "promote_prompt": "Reason to permanently trust {{ip}} (≥ 5 chars):",
    "approve": "Approve",
    "was": "was",
    "own_proposal": "your proposal",
    "x_in_30d": "{{n}}× over 30 d",
    "judge": {
      "score": "score",
      "div": "div",
      "dominant": "dominant"
    },
    "target_flow": "flow"
  },
  "host_baseline_policy": {
    "title": "Host Baseline Policy",
    "intro": "Runtime thresholds for the Host Behavioral Judge, per (tenant, probe role). Disabling mutes the judge for that role. k_enter/k_exit are the suspicion hysteresis (median + k·MAD); half_life is the cohort-trust ramp at cold-start. Applied asynchronously by the analytics job.",
    "col_role": "Role",
    "col_enabled": "Enabled",
    "col_updated": "Updated",
    "no_role": "(no role)",
    "none": "No policy set — all roles use defaults (k_enter 3.0 / k_exit 2.0 / half_life 24).",
    "k_enter_hint": "Enter suspicion above median + k_enter·MAD (0.5–10).",
    "k_exit_hint": "Exit suspicion below median + k_exit·MAD (0–10, ≤ k_enter).",
    "k_war_hint": "War-mode variant (⚔) — used instead of the standard k when this role's tenant is in war mode. Hardened defaults 1.8 / 1.2.",
    "half_life_hint": "Clean intervals for 50% own-profile trust vs cohort (1–1000).",
    "reason_label": "Reason (optional, audited)",
    "reason_placeholder": "Why this change? (recorded in the policy audit log)",
    "add_role_placeholder": "probe role",
    "save": "Save",
    "add": "Add",
    "confirm_delete": "Confirm?",
    "delete_hint": "Delete reverts this role to defaults (may re-enable a muted judge).",
    "version_hint": "policy_version — bumped by Reset to re-seed this role's baselines (read-only).",
    "reset_confirm": "Confirm?",
    "reset_hint": "Reset baselines: bumps policy_version → the job re-seeds (cold-start) this role's host baselines next pass. Audited.",
    "reset_done": "Baseline reset filed — re-seeds on the next analytics pass.",
    "saved": "Policy saved — applied on the next analytics pass.",
    "deleted": "Policy deleted.",
    "err_load": "Failed to load policies.",
    "err_save": "Failed to save the policy.",
    "err_invalid": "Invalid values: k_enter 0.5–10, k_exit 0–10 (≤ k_enter), half_life 1–1000.",
    "err_forbidden": "You are not allowed to edit this policy.",
    "err_no_tenant": "Narrow to a tenant scope to add a policy."
  },
  "fleet_shifts": {
    "title": "Fleet Shift Detector",
    "subtitle": "Coordinated cohort drift — audited shifts and operator reset (ADR-0033).",
    "intro": "A cohort dilutes a single compromised host, but a coordinated attack on most members of a role would move the \"normal\" itself. This detector audits those shifts and lets an operator reset a drifted cohort baseline. Reset is a human decision — never automatic — and is applied asynchronously by the analytics job on its next pass.",
    "shifts_title": "Audited shifts",
    "requests_title": "Filed reset requests",
    "none_shifts": "No fleet shift audited in the selected window.",
    "none_requests": "No reset request filed.",
    "col_role": "Role",
    "col_season": "Season",
    "col_trigger": "Trigger",
    "col_drift": "Drift",
    "col_members": "Members",
    "col_when": "When",
    "col_reason": "Reason",
    "col_by": "By",
    "col_status": "Status",
    "status_applied": "Applied",
    "status_pending": "Pending",
    "trigger_concurrent": "Concurrent activation",
    "trigger_drift": "Slow drift",
    "members_fmt": "{{suspicious}}/{{established}} suspicious",
    "reset_btn": "Reset baseline",
    "reset_title": "Reset cohort baseline — {{role}} / {{season}}",
    "reset_hint": "Re-seeds the cohort baseline from the current profile at the next job pass. Required: a reason (audited).",
    "reason_placeholder": "Why reset this cohort baseline? (e.g. legitimate fleet-wide deploy at 14:00)",
    "submit": "File reset",
    "cancel": "Cancel",
    "filed_ok": "Reset request filed — applied on the next analytics pass.",
    "err_load": "Failed to load fleet shifts.",
    "err_forbidden": "You are not allowed to reset this cohort.",
    "err_reason": "A reason is required.",
    "err_submit": "Failed to file the reset request."
  },
  "incidents": {
    "title": "Incidents",
    "subtitle": "Attack narratives — post-path timelines assembled from hot- and post-path verdicts (ADR-0048). Observation only, not a detection.",
    "filter_source": "Source IP…",
    "family_any": "Any family",
    "multi_stage_only": "Multi-stage only",
    "clear": "Clear",
    "loading": "Loading incidents…",
    "none": "No incident in the selected scope.",
    "no_stages": "No attributed stage",
    "events_fmt": "{{count}} events",
    "related_fmt": "{{count}} related events",
    "judges_label": "Judges",
    "origins_label": "Origins",
    "population_admitted": "Include episodes admitted without an alert",
    "admitted_no_alert": "Admitted, no alert",
    "admitted_via_fmt": "Admitted via {{source}}",
    "err_load": "Failed to load incidents.",
    "open_events_title": "Open this source's events in Anomalies (full window)"
  },
  "red_team": {
    "title": "Red Team Simulator",
    "subtitle": "Automated attack simulation against the LSTM + GNN detection pipeline",
    "internal_use_only": "Internal use only",
    "attack_scenarios": "Attack Scenarios",
    "expected_ttd": "Expected TTD: {{ttd}}",
    "simulating": "Simulating...",
    "run_one": "Run {{n}} scenario",
    "run_many": "Run {{n}} scenarios",
    "select_to_run": "Select scenarios and run simulation",
    "detected": "DETECTED",
    "evaded": "EVADED",
    "lstm_score": "LSTM score",
    "gnn_score": "GNN score",
    "consensus": "Consensus",
    "ttd": "TTD",
    "packets": "Packets",
    "needs_retraining": "Model needs retraining — add to shadow dataset",
    "stress": {
      "title": "Normalization Stress (ADR-0015)",
      "subtitle_prefix": "Visualizes the 10D feature vector produced by ",
      "subtitle_code1": "flow.rs::to_feature_vector",
      "subtitle_middle": " on 5 out-of-bounds scenarios. Helps you feel where the v1.6 normalization \"squashes\" signal before pushing these scenarios through ONNX (see ",
      "subtitle_code2": "make ai-stress-normalization",
      "subtitle_suffix": ").",
      "description_label": "Description:",
      "expected_label": "Expected:",
      "vector_label": "Produced vector (10D)",
      "saturated_slots": "Saturated slots ({{n}})",
      "sentinel_slots": "Sentinels ({{n}})",
      "badge": {
        "ok": "ok",
        "saturated": "saturated",
        "sentinel": "sentinel",
        "nonfinite": "nonfinite"
      }
    },
    "category": {
      "reconnaissance": "Reconnaissance",
      "exfiltration": "Exfiltration",
      "c2": "C2",
      "lateral_movement": "Lateral Movement"
    }
  },
  "console_3d": {
    "title": "3D Network Console",
    "subtitle_prefix": "Live topology — scores via ",
    "subtitle_suffix": " · click a node for XAI explanation",
    "api_down": "API unreachable (mock fallback)",
    "live_status": "{{live}}/{{total}} live nodes",
    "anomaly_score": "Anomaly Score",
    "score_legend": {
      "critical": "> 0.8  Critical",
      "high": "> 0.5  High",
      "medium": "> 0.3  Medium",
      "normal": "≤ 0.3  Normal"
    },
    "empty_panel_hint": "Click a node to see its latest event + XAI explanation.",
    "nodes": "Nodes",
    "selected_node": "Selected node",
    "score": "Score",
    "live_time": "live · {{time}}",
    "fallback_no_event": "fallback (no recent event for this IP)",
    "judge_label": "judge:",
    "explanation": "Explanation",
    "webgl_unavailable_title": "3D visualization unavailable",
    "webgl_unavailable_body": "This browser has no WebGL context. The graph view is disabled; the side panel and event feed remain usable.",
    "webgl_render_error_title": "3D render failed",
    "webgl_render_error_body": "A rendering error was caught (invalid asset or driver issue). Reload the page to retry.",
    "api_pending": "Connecting…"
  },
  "post_quantum": {
    "title": "Post-Quantum Cryptography",
    "subtitle": "NIST FIPS 203 (ML-KEM) + FIPS 204 (ML-DSA) · real backend calls",
    "quorum_ready": "Quorum ready",
    "stats": {
      "public_key": "Public key",
      "secret_key": "Secret key",
      "ciphertext": "Ciphertext",
      "shared_secret": "Shared secret",
      "signature": "Signature",
      "keygen": "Keygen",
      "ss_encap": "SS (encap)",
      "ss_decap": "SS (decap)",
      "encap_latency": "Encap latency",
      "decap_latency": "Decap latency",
      "sign_latency": "Sign latency",
      "size": "Size"
    },
    "kem": {
      "title": "ML-KEM-768 — key encapsulation demo",
      "generate_keypair": "Generate keypair",
      "generating": "Generating...",
      "encapsulate": "Encapsulate",
      "encapsulating": "Encapsulating...",
      "decapsulate": "Decapsulate",
      "decapsulating": "Decapsulating...",
      "ss_match": "✓ Shared secrets match — both peers now share 32 bytes of keying material.",
      "ss_differ": "✗ Shared secrets DIFFER — possible attack or wrong key pair."
    },
    "sign": {
      "title": "ML-DSA-65 — signature demo",
      "generate_keypair": "Generate signing keypair",
      "sign": "Sign",
      "signing": "Signing...",
      "verify": "Verify",
      "verifying": "Verifying...",
      "message": "Message",
      "valid": "valid",
      "invalid": "INVALID",
      "verdict_template": "Signature {{verdict}} — {{ms}}ms"
    }
  },
  "entropy": {
    "title": "Entropy Analyzer",
    "subtitle": "Shannon entropy — H(X) = −Σ p(xᵢ) log₂ p(xᵢ). Unit: bits / byte.",
    "input_payload": "Input payload",
    "presets": {
      "Texte naturel": "Natural text",
      "JSON API": "JSON API",
      "Base64 (compressé)": "Base64 (compressed)",
      "Aléatoire (chiffré)": "Random (encrypted)",
      "Zéros (très structuré)": "Zeros (very structured)"
    },
    "input_placeholder": "Paste text, binary (hex), base64, …",
    "input_stats": "{{total}} bytes analyzed · {{distinct}} distinct values out of 256",
    "entropy_label": "Entropy",
    "entropy_max": "/ 8.000 bits",
    "anm_threshold_title": "ANM threshold {{value}}",
    "verdict": {
      "quasi_random": "QUASI-RANDOM",
      "quasi_random_reason": "Distribution near uniform — compression/encryption likely.",
      "above_anm": "ABOVE ANM THRESHOLD",
      "above_anm_reason": "H ≥ {{value}} → candidate for exfiltration alert (combined with I/O ratio).",
      "high": "HIGH ENTROPY",
      "high_reason": "Possibly compressed binary. Correlate with context.",
      "mixed": "MIXED",
      "mixed_reason": "Text, JSON, protocols — standard profile.",
      "structured": "STRUCTURED",
      "structured_reason": "Very repetitive — likely padding, zeros, or limited palette."
    },
    "distribution_title": "Distribution (16-byte bins)",
    "baselines_title": "References — content types",
    "your_payload": "Your payload",
    "threshold_label": "threshold {{value}}",
    "baseline_labels": {
      "ascii_code_json": "ASCII, code, JSON",
      "natural_text": "Natural text FR/EN",
      "compressed_binary": "Compressed binary",
      "random_encrypted": "Random / encrypted"
    },
    "pipeline_title": "How ANM uses this metric",
    "pipeline": {
      "li1_strong": "entropy proxy",
      "li1_text1": " per flow (",
      "li1_code1": "FlowRecord::entropy_proxy",
      "li1_text2": " in ",
      "li1_code2": "backend/ebpf/src/flow.rs",
      "li1_text3": ") — bytes/packet ratio normalized, not full Shannon to stay no-FP in eBPF kernel.",
      "li1_prefix": "The eBPF probe computes an ",
      "li2_strong": "8D feature vector",
      "li2_prefix": "The proxy joins the ",
      "li2_suffix": " (index 1) consumed by LSTM + GNN. The LSTM detects abnormal entropy sequences, the GNN sees topological correlation.",
      "li3_strong": "RuleJudge",
      "li3_prefix": "The ",
      "li3_text1": " (third judge, anti-adversarial) also checks an absolute threshold: ",
      "li3_text2": " (normalized proxy) → flag ",
      "li3_text3": " even when ML judges stay silent.",
      "li4_strong": "I/O ratio",
      "li4_prefix": "Correlation with ",
      "li4_suffix": ": a high-entropy flow + outbound/inbound > 3× = strong encrypted exfiltration signal."
    },
    "top_bytes_title": "Top 16 bytes contributing to H"
  },
  "lstm_gnn_wasm": {
    "title": "LSTM + GNN — WASM Inference",
    "subtitle": "ONNX Runtime Web — in-browser ML inference, zero round-trip",
    "lstm_judge": "LSTM Judge",
    "gnn_judge": "GNN Judge",
    "model_status": {
      "loaded": "loaded",
      "simulation": "simulation",
      "not_loaded": "not loaded"
    },
    "features_title": "Network Flow Features",
    "feature_labels": {
      "packet_rate": "Packet Rate",
      "byte_entropy": "Byte Entropy",
      "flow_duration": "Flow Duration",
      "port_diversity": "Port Diversity",
      "payload_ratio": "Payload Ratio",
      "inter_arrival": "Inter-Arrival",
      "retransmits": "Retransmits",
      "ttl_variance": "TTL Variance"
    },
    "run_inference": "Run Inference",
    "inferring": "Inferring...",
    "results_title": "Inference Results",
    "consensus": "Consensus",
    "latency": "Inference latency",
    "verdict": "Verdict",
    "verdict_critical": "CRITICAL",
    "verdict_anomaly": "ANOMALY",
    "verdict_normal": "NORMAL",
    "runtime": "Runtime",
    "runtime_value": "WASM (in-browser)",
    "attack_family": "Attack family",
    "empty_hint": "Adjust features and click \"Run Inference\""
  },
  "federated": {
    "title": "Federated Learning",
    "subtitle": "Privacy-preserving distributed training — DP ε={{epsilon}}",
    "round_label": "Round {{n}}",
    "kpi": {
      "active_nodes": "Active Nodes",
      "total_samples": "Total Samples",
      "global_loss": "Global Loss",
      "dp_epsilon": "DP Epsilon"
    },
    "convergence": "Global Loss Convergence",
    "participants": "Participant Nodes",
    "samples_suffix": "samples",
    "loss_suffix": "loss",
    "status": {
      "training": "training",
      "syncing": "syncing",
      "idle": "idle"
    }
  },
  "sbom": {
    "title": "Software Bill of Materials",
    "subtitle": "Dependency graph + CVE tracking",
    "kpi": {
      "components": "Components",
      "with_vulns": "With Vulns",
      "total_cves": "Total CVEs",
      "licenses": "Licenses"
    },
    "search_placeholder": "Search component...",
    "severity": {
      "all": "All severities",
      "clean": "Clean",
      "low": "Low",
      "medium": "Medium",
      "high": "High"
    },
    "th": {
      "component": "Component",
      "version": "Version",
      "license": "License",
      "supplier": "Supplier",
      "vulnerabilities": "Vulnerabilities"
    },
    "clean_badge": "clean",
    "cve_count": "{{n}} CVE"
  },
  "adn": {
    "title": "Anomaly Detection Network",
    "subtitle_prefix": "LSTM + GNN dual-judge — live from ",
    "api_unreachable": "API unreachable",
    "live": "Live",
    "kpi": {
      "events_50": "Events (last 50)",
      "events": "Events (window)",
      "critical": "Critical",
      "disagreement_xai": "Disagreement (XAI)"
    },
    "range": {
      "label": "Window",
      "1h": "1 h",
      "6h": "6 h",
      "24h": "24 h",
      "7d": "7 d",
      "30d": "30 d",
      "all": "All",
      "capped": "capped ({{n}})",
      "capped_title": "Counts are lower bounds — only the most recent {{n}} ingested events are loaded (API orders by ingestion, not event time), so older events in this window may be missing.",
      "episode": "episode {{from}} → {{to}}",
      "episode_title": "Absolute window of one episode, pushed server-side (before the LIMIT) — it replaces the relative range",
      "episode_clear": "Clear the episode window"
    },
    "scores_chart": "Individual scores — selected window",
    "loading": "Loading…",
    "no_event_to_display": "No event to display",
    "anomaly_events": "Anomaly events ({{n}})",
    "view_all": "All",
    "view_needs_review": "To review (XAI)",
    "filter": {
      "confidence": "Confidence",
      "severity": "Severity",
      "action": "Action",
      "judge": "Judge",
      "source_ip": "Source IP",
      "family": "Attack family",
      "min_score": "Min score"
    },
    "remove_filter_aria": "Remove {{label}} filter",
    "clear_filters_title": "Clear all filters",
    "clear_filters_button": "clear",
    "empty": {
      "no_review": "No event to review (no divergence reported)",
      "no_match": "No event matches the {{n}} active filter(s)",
      "no_recent": "No recent event"
    },
    "click_for_xai": "Click an event to see its XAI explanation and associated policies.",
    "dctx": {
      "rule": {
        "C2_beacon": "C2 beacon (regular cadence)",
        "C2_hammer": "C2 burst (hammer)",
        "recon_horizontal": "horizontal recon",
        "recon_horizontal_outbound": "horizontal recon (outbound)",
        "exfil_volume": "exfiltration (volume)",
        "exfil_adaptive": "adaptive exfiltration (vs baseline)",
        "bruteforce_inbound": "inbound brute-force",
        "bruteforce_hammer": "brute-force (burst)",
        "bruteforce_success": "successful brute-force",
        "lateral_sequence": "lateral movement",
        "web_campaign": "web campaign"
      },
      "dir": {
        "inbound": "inbound",
        "outbound": "outbound"
      },
      "cvq": {
        "periodic": "near-periodic",
        "regular": "regular",
        "irregular": "irregular",
        "bursty": "very irregular (bursty)"
      },
      "window": "window {{v}}",
      "beats": "{{v}} beats",
      "connections": "{{v}} connections",
      "dst": "{{v}} distinct destinations",
      "fails": "{{v}} failed attempts",
      "successes": "{{v}} successes",
      "cv": "CV {{v}}{{q}}",
      "volume_z": "volume z-score {{v}}",
      "port_entropy": "port entropy {{v}}",
      "score": "score {{v}}%",
      "action": "action: {{v}}",
      "raw": "raw detail",
      "dest": "→ {{v}}",
      "buckets": "{{v}} buckets",
      "exfil_bytes": "{{v}} exfiltrated",
      "traffic_bytes": "{{v}} of traffic"
    },
    "multi_host": "multi-host",
    "multi_host_hint": "The rule measured {{count}} destinations for this source.",
    "incident_narrative": "Incident narrative",
    "incident_narrative_title": "Open this source's post-path narrative in Incidents (ordered stages, full horizon)"
  },
  "war_mode": {
    "title": "War Mode Ceremony",
    "subtitle": "3 independent levels — thresholds tighten as soon as at least one is active. Each level requires 2 distinct approvals (4-eyes).",
    "no_scope_available": "No war-mode scope available — pick an MSSP / tenant in the scope bar or contact your admin.",
    "section": {
      "platform": "Platform",
      "mssp": "MSSP",
      "tenant": "Tenant",
      "platform_subtitle": "Ward³ vendor override — affects ALL MSSPs and their tenants",
      "mssp_subtitle": "Hardening for this MSSP — affects ALL its tenants (id: {{id}}…)",
      "tenant_subtitle": "Local hardening for tenant {{id}}…"
    },
    "status": {
      "scope_label": "{{label}} scope",
      "api_unreachable": "API unreachable",
      "war_active": "{{label}} WAR MODE ACTIVE",
      "stable": "Stable",
      "thresholds_hardened": "hardened thresholds",
      "expires": "expires {{date}}",
      "approvals_count": "{{count}}/{{required}} approvals",
      "loading": "loading…"
    },
    "give_approval": "Give your approval ({{label}})",
    "your_approval": "Your approval",
    "active_since": "Active since",
    "reason_quote": "Reason:",
    "remove_approval": "Remove my approval",
    "submitting": "…",
    "reason_placeholder": "Justify {{label}} activation (10 chars min)",
    "confirm_platform": "I confirm this Platform hardening will impact ALL MSSPs and their tenants.",
    "confirm_mssp": "I confirm this MSSP hardening will impact ALL tenants of this MSSP.",
    "approve_button": "I approve {{label}} War Mode",
    "read_only_role": "Read only — your role does not allow approving at this level.",
    "reason_too_short": "Justification too short (10 chars min).",
    "confirm_required": "Tick the confirmation box — the impact reaches beyond your direct scope.",
    "approval_recorded": "Approval recorded.",
    "approval_revoked": "Approval removed.",
    "forbidden_act": "403 — Your role does not allow acting on this scope.",
    "forbidden_revoke": "403 — You cannot revoke this approval.",
    "server_error": "Server error",
    "slot_label": "Slot {{n}}",
    "slot_admin": "admin #{{id}}…",
    "slot_expires": "expires {{time}}",
    "slot_waiting": "Pending…",
    "explanation": {
      "title": "How it works",
      "li1_strong": "3 independent levels",
      "li1_text": " — Platform (red), MSSP (orange), Tenant (yellow). The effective threshold in ",
      "li1_code": "ingest_event",
      "li1_text2": " = OR over the 3 flags, so activating ",
      "li1_em": "any one",
      "li1_text3": " hardens scoring for events matching that scope.",
      "li2_strong": "4-eyes principle",
      "li2_text": " — each level requires 2 distinct admins in a 1 h sliding window. Approvals expire automatically.",
      "li3": "Identical hardened thresholds across the 3 levels: block > 0.75, quarantine > 0.55, ratelimit > 0.3 (vs. 0.9 / 0.7 / 0.5 in stable mode).",
      "li4": "Explicit confirmation required before Platform / MSSP — the UI forces the check to mitigate accidental activation (R21).",
      "li5_prefix": "Full audit: every approval/revocation is logged structurally. For details, query "
    }
  },
  "trusted_sources": {
    "title": "Trusted Sources",
    "subtitle": "Permanent whitelist — sources promoted from repeated suppressions (≥ 3 over 30 d). No policy is derived for these IPs, even with score 1.0. Layer 0 tags a flow when its canonical initiator (src) is in this list — matched on the initiator, in any direction (egress can tag a local outbound initiator too); only Pattern A's drop is limited to inbound.",
    "loading": "Loading…",
    "admin_only": "admin only",
    "promote": "promote",
    "revoke": "revoke",
    "renew": "renew",
    "renew_title": "Renew 1 h",
    "cidr_badge": "CIDR",
    "x_in_30d": "{{n}}× over 30d",
    "promoted_from": "{{n}}× suppress",
    "promoted_direct": "direct",
    "renewed_from": "renewed (#{{id}})",
    "initial": "initial",
    "candidates_section": "Promotion candidates",
    "candidates_help": "Sources with ≥ 3 distinct suppressions in a rolling 30 d. Promoting = permanent whitelist, use for structural FPs (backup, monitoring, recurring audit).",
    "candidates_empty": "No candidate. An IP must accumulate ≥ 3 suppressions over 30 d to appear here.",
    "active_trusted_section": "Active trusted",
    "active_trusted_empty_prefix": "No trusted source. Promote a candidate above, or (admin) via ",
    "active_trusted_empty_code": "POST /api/trusted-sources",
    "active_trusted_empty_suffix": ".",
    "suppressions_section": "Active suppressions",
    "suppressions_help_prefix": "Transient TTL suppressions (created from the drawer on ",
    "suppressions_help_code1": "/anomalies",
    "suppressions_help_middle": "). \"Renew\" creates a new 1 h suppression chained via ",
    "suppressions_help_code2": "renewed_from_id",
    "suppressions_help_suffix": " to preserve the audit trail.",
    "suppressions_empty_prefix": "No active suppression. Suppressions are created from the event drawer in ",
    "suppressions_empty_code": "/anomalies",
    "suppressions_empty_suffix": ".",
    "prompts": {
      "promote": "Promote {{ip}} to trusted? Reason (≥ 5 chars):",
      "revoke_trusted": "Revoke trusted {{ip}}? Score≥0.9 events will resume normal derivation.",
      "revoke_suppression": "Revoke suppression {{ip}}?"
    },
    "th": {
      "source_ip": "Source IP",
      "frequency": "Frequency",
      "reasons": "Reasons",
      "last": "Last",
      "reason": "Reason",
      "promoted_from_h": "Promoted from",
      "created": "Created",
      "expires": "Expires",
      "origin": "Origin"
    }
  },
  "baselines": {
    "title": "Baselines",
    "subtitle_prefix": "Long-term stateful profiles per (src, dst, port) triplet. Online Welford stats (μ + σ packets), 24h heatmap of active hours. Feeds the ",
    "subtitle_suffix": " flags visible in <code>/anomalies</code>.",
    "hour_active": "{{h}}h UTC: active",
    "hour_never_seen": "{{h}}h UTC: never seen",
    "drift_section": "Drift PSI baselines (per-tenant)",
    "tenants_count_one": "{{count}} tenant",
    "tenants_count_other": "{{count}} tenants",
    "loading": "Loading…",
    "drift_empty_prefix": "No baseline yet. Phase 5c will wire ",
    "drift_empty_suffix": " to calibrate the PSI distribution on the first 500 samples per tenant. Inference stays functional in the meantime but without per-tenant drift alerts.",
    "tenant_label": "tenant {{prefix}}…",
    "features_calibrated": "{{n}} / 10 calibrated features",
    "back_to_top": "Back to top",
    "triplets_for": "Triplets for",
    "no_triplets": "No triplet for this source.",
    "active_sources_24h": "Active sources over 24h",
    "no_baseline_prefix": "No baseline yet. The tracker starts as soon as flows arrive and ",
    "no_baseline_suffix": " is configured on the inference daemon.",
    "feature_distribution": "feature {{n}} distribution",
    "th": {
      "destination": "Destination",
      "port": "Port",
      "flows": "Flows",
      "packet_stats": "Packet stats",
      "bytes": "Bytes",
      "active_hours": "Active hours (UTC)",
      "last": "Last",
      "source_ip": "Source IP",
      "distinct_dests": "Distinct destinations",
      "total_flows": "Total flows",
      "total_bytes": "Total bytes",
      "last_activity": "Last activity"
    }
  },
  "family_thresholds": {
    "title": "Family Thresholds",
    "subtitle_part1": "Tune Block/Quarantine sensitivity per attack family for this tenant. ",
    "subtitle_strong": "Hardening only",
    "subtitle_part2": " — overrides are rejected if they relax the MSSP baseline.",
    "refresh": "Refresh",
    "loading": "Loading…",
    "composition_hint_prefix": "Runtime composition: ",
    "composition_hint_suffix": ". Standard actions (Block > 0.9 / Quarantine > 0.7 / RateLimit > 0.5) and war-mode (0.75 / 0.55 / 0.3) each use their own multiplier — edit the 2 columns per context.",
    "th": {
      "family": "Family",
      "baseline": "Baseline (MSSP)",
      "override_std": "Your override (std)",
      "override_war": "Your override (war)",
      "effective": "Effective"
    },
    "not_set": "not set",
    "override_badge": "override",
    "save": "Save",
    "saved": "Saved",
    "delete_title": "Delete the override (reverts to MSSP baseline)",
    "show_audit": "Show audit log",
    "hide_audit": "Hide audit log",
    "audit": {
      "th_when": "When",
      "th_family": "Family",
      "th_column": "Column",
      "th_change": "Change",
      "th_action": "Action",
      "th_by": "By",
      "loading": "Loading audit log…",
      "empty": "No audit entry for this tenant."
    },
    "errors": {
      "forbidden_get": "403 — not authorized.",
      "multiplier_range": "Multiplier must be in (0, {{max}}].",
      "below_baseline_std": "std must be ≥ MSSP baseline ({{value}}).",
      "below_baseline_war": "war must be ≥ MSSP baseline ({{value}}).",
      "relaxation_forbidden": "Tenant can only harden above the baseline.",
      "forbidden_write": "403 — not authorized to modify this override.",
      "delete_404": "No override to delete."
    },
    "family_meta": {
      "DoS_DDoS": "Volumetric / app-layer floods",
      "BruteForce": "Password spraying",
      "Recon": "Port scans, host enumeration",
      "C2_Botnet": "Command & control beacons",
      "WebInjection": "SQLi, XSS, RCE",
      "Persist_Exploit": "Lateral movement",
      "Other": "Unclassified traffic (HybridJudge below threshold or LSTM legacy) — does NOT cover typos/drift"
    }
  },
  "models": {
    "title": "Models registry",
    "subtitle": "Registered models · canary rollout · observed distribution",
    "canary": {
      "section": "Canary distribution",
      "metrics_unreachable": "/metrics inference unreachable — {{message}}. Verify port 9194 is exposed (dev compose) or add a K8s proxy.",
      "no_batch": "No batch routed yet. Canary appears after ~32 scored flows.",
      "split_summary": "stable {{stable}} · canary {{canary}}",
      "observed_ratio": "Observed canary ratio: <strong>{{pct}} %</strong> · Law of large numbers: converges to configured pct on N≥100 batches."
    },
    "drift": {
      "section": "PSI drift per feature (8 dim)",
      "thresholds": "thresholds: < 0.1 stable · 0.1–0.2 watch · ≥ 0.2 significant drift",
      "no_data": "No drift measurement. The tracker requires `ANM_DRIFT_WARMUP_SAMPLES` (default 500) before publishing.",
      "feature_label": "feature {{n}}"
    },
    "registry": {
      "section": "Registry",
      "endpoint_unavailable": "Endpoint <code>/api/models</code> not available — use <code>docker compose exec inference anm-model-cli list</code> via CLI in the meantime.",
      "empty": "No model registered. <code>make ai-register VERSION=…</code>",
      "th_active": "Active",
      "th_pct": "%",
      "th_kind": "Kind",
      "th_version": "Version",
      "th_acc": "Acc",
      "th_created": "Created"
    }
  },
  "shadow_dashboard": {
    "title": "Shadow Mode Dashboard",
    "subtitle": "Real-world traffic collection + automatic labeling pipeline",
    "collecting": "Collecting",
    "kpi": {
      "events_visible": "Events visible",
      "events_visible_hint": "{{n}} most recent (API cap)",
      "to_review": "To review (low/disag)",
      "to_review_hint": "non-HIGH confidence",
      "drift_canary": "Drift PSI · Canary",
      "drift_canary_link": "See",
      "drift_canary_hint": "8 features · stable/canary ratio"
    },
    "recent_events": "Recent events",
    "review_only_toggle": "Review only (low / disagreement confidence)",
    "api_unreachable": "API /api/events unreachable — {{message}}",
    "unknown_error": "unknown error",
    "loading": "Loading…",
    "empty_review": "No events to review (all judges agree)",
    "empty_recent": "No events captured recently",
    "click_to_expand": "Click to see the explanation",
    "no_xai": "Event pre-dates XAI migration (no stored explanation)."
  },
  "honey_data": {
    "title": "Honey Data",
    "subtitle": "Synthetic honeypot — capturing real attack patterns",
    "sessions_captured": "sessions captured",
    "live_feed": "Live Attack Feed",
    "capturing": "Capturing",
    "port": "Port {{n}}",
    "hits": "hits",
    "captured": "captured",
    "status": {
      "active": "active",
      "idle": "idle"
    }
  },
  "overview": {
    "title": "Ward³ Overview",
    "subtitle": "Real-time health · click a card to drill down",
    "api_error": "API error — {{message}}",
    "refreshed": "Refreshed",
    "kpi": {
      "events_per_min": "Events / min (1 min)",
      "events_in_window": "{{n}} in window",
      "critical": "Critical (window)",
      "critical_hint": "{{anomaly}} anomaly · {{suspect}} suspect",
      "disagreement_rate": "Disagreement rate",
      "disagreement_hint": "LSTM vs GNN — fail-closed consensus",
      "blocked_sources": "Blocked sources",
      "in_quarantine": "{{n}} in quarantine",
      "honey_hits": "Honey-trap hits (24 h)",
      "honey_hits_hint": "0 hits expected when nominal",
      "active_model": "Active judges",
      "active_model_hint": "Judges scoring in this window"
    },
    "chart": {
      "title": "Event volume",
      "hint_window": "over {{window}} · cap 1000 ingested"
    },
    "loading": "Loading…",
    "capped": "≥ {{n}} (capped)",
    "range": {
      "label": "Window",
      "1h": "1 h",
      "6h": "6 h",
      "24h": "24 h",
      "7d": "7 d",
      "30d": "30 d",
      "all": "All"
    },
    "posture": {
      "level": {
        "elevated": "ELEVATED",
        "watch": "WATCH",
        "nominal": "NOMINAL",
        "quiet": "QUIET",
        "partial": "PARTIAL"
      },
      "summary": "{{rate}} events/min · {{window}} in window · {{disagree}} disagreement · {{critical}} critical · {{honey}} honeypot hits (24 h)"
    },
    "confidence": {
      "title": "Confidence split",
      "high": "High",
      "low": "Low",
      "disagreement": "Disagreement",
      "not_measured": "ML not run",
      "unknown": "N/A"
    },
    "families": {
      "title": "Top attack families",
      "none": "No family in this window",
      "link": "Filter anomalies on {{family}}"
    },
    "on_loaded": "on {{n}} loaded"
  },
  "probe_setup": {
    "title": "Probe Setup",
    "subtitle": "Generate a one-liner to enroll a new eBPF probe on a customer host.",
    "tenant_label": "Tenant",
    "ttl_label": "Token validity",
    "ttl_24h": "24 hours",
    "ttl_48h": "48 hours",
    "ttl_7d": "7 days",
    "generate": "Generate install command",
    "regenerate": "Regenerate",
    "copy": "Copy",
    "copied": "Copied!",
    "copy_failed": "Clipboard unavailable — copy the command manually.",
    "expires": "Expires {{when}}",
    "reuse_note": "Reusable until expiry — treat it like a secret.",
    "loading": "Loading…",
    "empty": "No tenants for this MSSP yet.",
    "pick_mssp": "Select an MSSP in the scope selector (top bar) to continue.",
    "forbidden": "You need the mssp_admin or platform_admin role to generate probe enrollment tokens.",
    "errors": {
      "forbidden": "403 — you are not allowed to generate a probe token for this tenant.",
      "forbidden_list": "403 — not authorized to list this MSSP's tenants.",
      "not_found": "404 — MSSP or tenant not found.",
      "generic": "Failed to generate the install command."
    }
  },
  "campaigns": {
    "title": "Campaigns",
    "subtitle": "Intra-tenant clusters — alerting sources converging on the same rare destination (ADR-0048). Each member carries its own local signal (anti guilt-by-association). Observation only.",
    "loading": "Loading campaigns…",
    "none": "No campaign cluster in the selected scope.",
    "dst_label": "Destination",
    "sources_fmt": "{{count}} sources",
    "families_label": "Families",
    "origins_label": "Origins",
    "events_fmt": "{{count}} events",
    "err_load": "Failed to load campaigns."
  },
  "model_health": {
    "title": "Model Health",
    "subtitle": "Per-tenant model distributions — what Prometheus does poorly (family mix, abstention, disagreement, score quantiles, judge sentinels). Pre-flight before an ONNX push. Observation only.",
    "loading": "Loading model health…",
    "none": "No model health snapshot in the selected scope.",
    "events_fmt": "{{count}} events",
    "empty_family": "Empty family",
    "disagreement": "Disagreement",
    "lstm_sentinel": "LSTM sentinel",
    "gnn_sentinel": "GNN sentinel",
    "score_p50": "Score p50",
    "score_p90": "Score p90",
    "score_p99": "Score p99",
    "err_load": "Failed to load model health."
  },
  "cost_budget": {
    "title": "Cost / Risk Budget",
    "subtitle": "Per-tenant cost — raw_flows scan volume, downstream noise, noisiest sources (ADR-0048). Observation only.",
    "loading": "Loading cost snapshots…",
    "none": "No cost snapshot in the selected scope.",
    "col_tenant": "Tenant",
    "col_flows": "Flows",
    "col_bytes": "Bytes",
    "col_probes": "Probes",
    "col_alerts": "Alerts",
    "col_top_source": "Top source",
    "col_window": "Window",
    "err_load": "Failed to load cost snapshots."
  },
  "suppression_suggestions": {
    "title": "Suppression Hints",
    "subtitle": "Recurrent low-risk noise never escalated — candidate suppressions mined from anomaly_events (ADR-0048).",
    "suggestion_only": "Suggestion only — this view never writes policy. Create a real suppression from the Suppressions module.",
    "loading": "Loading suggestions…",
    "none": "No suppression candidate in the selected scope.",
    "col_signature": "Signature",
    "col_events": "Events",
    "col_days": "Days",
    "col_max_score": "Max score",
    "col_family": "Sample family",
    "col_window": "Window",
    "err_load": "Failed to load suppression hints."
  },
  "arbiter": {
    "title": "Digital Twin Arbiter",
    "subtitle": "Post-decision arbiter for the gray zone (ADR-0050): long-horizon evidence, native-grain re-scoring, replayable dossiers, 4-eyes relabelling.",
    "not_a_judge": "Not a judge — the arbiter never sees live flows, never enforces. Outputs are proposals only (4-eyes) and TTL suppression suggestions.",
    "tab_queue": "Queue",
    "tab_cases": "Dossiers",
    "tab_suggestions": "Suggestions",
    "loading": "Loading…",
    "err_load": "Failed to load arbiter data.",
    "err_action": "Action failed.",
    "queue_empty": "Queue is empty — the gray zone is quiet (or ANM_TWIN_ARBITER is off).",
    "cases_empty": "No dossiers yet. Refer an event from Anomalies or a benign flow from Flows.",
    "suggestions_empty": "No suppression suggestions (benign verdicts in active mode produce them).",
    "col_origin": "Origin",
    "col_target": "Target",
    "col_source": "Source",
    "col_score": "Score",
    "col_status": "Status",
    "col_created": "Created",
    "col_model": "Model",
    "col_frac": "frac>thr",
    "col_windows": "Windows",
    "origin_manual": "manual",
    "origin_auto": "auto",
    "window": "Window",
    "evidence_hash": "evidence",
    "anchor": "Anchor",
    "rescoring": "Native-grain re-scoring (§D4-bis)",
    "rescoring_note": "Input to the dossier only — never a verdict gate (D7.3). Score distribution across full native windows; inter-window stability is the new information.",
    "notes": "Notes (named degradations)",
    "show_evidence": "Show raw evidence (replayable dossier)",
    "hide_evidence": "Hide raw evidence",
    "case_not_found": "Dossier not found (purged or out of scope).",
    "view_case": "view dossier #{{id}}",
    "suggestion_only": "Suggestion-only: applying creates a real suppression via /api/suppressions (admin + RBAC) — never automatic.",
    "apply": "Apply",
    "dismiss": "Dismiss",
    "applied": "Suppression created for {{ip}}.",
    "dismissed": "Suggestion dismissed.",
    "already_decided": "Already decided by another analyst.",
    "apply_prompt": "Suppression reason for {{ip}} (≥ 5 chars, audited):",
    "admin_only": "admin only",
    "applied_close_failed": "Suppression created for {{ip}}, but closing the suggestion failed — it may still show as suggested.",
    "col_conformal": "conformal",
    "conformal_none": "No calibrated p-value: conformal layer off, no stratum matching this artifact for this tenant, or starved calibration (see dossier notes)."
  },
  "users": {
    "title": "Users",
    "subtitle": "Accounts of this tenant — create, change role, reset password. Cross-tenant (MSSP) users are managed in the admin console.",
    "new_user": "New user",
    "cancel": "Cancel",
    "email": "Email",
    "password": "Password",
    "role": "Role",
    "create": "Create",
    "loading": "Loading…",
    "empty": "No user in this tenant yet.",
    "sa_badge": "service",
    "you_badge": "you",
    "reset_password": "Reset password",
    "delete": "Delete user",
    "roles": {
      "tenant_admin": "Tenant admin",
      "tenant_viewer": "Viewer (read-only)"
    },
    "prompts": {
      "reset_password": "New password for {{email}} (min. 8 characters) — all their sessions are revoked (in-flight access tokens are cut within ~30 s):",
      "delete": "Delete {{email}}? All their sessions are revoked (in-flight access tokens are cut within ~30 s)."
    },
    "errors": {
      "forbidden": "403 — not authorized to view this tenant's users.",
      "password_short": "Password too short (min. 8 characters)."
    },
    "th": {
      "email": "Email",
      "role": "Role",
      "created": "Created"
    }
  },
  "flows": {
    "title": "Flows",
    "subtitle": "Raw flows captured by the probe (XDP ingress + tc-egress). Expand a flow to see correlated alerts.",
    "refresh": "Refresh",
    "loading": "Loading…",
    "empty": "No flow (egress/L7 capture active? scope?).",
    "count": "{{shown}} flows",
    "badge_alert": "This flow has a correlated anomaly (expand for detail)",
    "alerts_loading": "alerts…",
    "no_alert": "No alert correlated to this flow.",
    "open_in_anomalies": "Open in Anomalies (source {{ip}})",
    "filter": {
      "src": "src_ip (initiator)",
      "dst": "dst_ip",
      "service": "Service",
      "service_all": "All services",
      "proto": "Protocol",
      "proto_all": "All protocols",
      "dir": "Direction",
      "dir_all": "All directions",
      "dir_inbound": "Inbound",
      "dir_outbound": "Outbound",
      "only_alerts": "With alert only"
    },
    "sort": {
      "label": "Sort",
      "start": "Recent",
      "bytes": "Bytes",
      "packets": "Packets"
    },
    "range": {
      "label": "Window",
      "1h": "1h",
      "6h": "6h",
      "24h": "24h",
      "15m": "15 min"
    },
    "col": {
      "flow": "Flow (src → dst)",
      "proto": "Proto",
      "service": "Service",
      "dir": "Dir",
      "bytes": "Bytes",
      "pkts": "Pkts",
      "l7": "L7",
      "start": "Start"
    },
    "error": {
      "forbidden": "Access denied.",
      "generic": "fetch failed"
    },
    "refer": {
      "queued": "✓ referral queued (#{{id}}) — case under /arbiter",
      "duplicate": "already queued (#{{id}})",
      "button": "Refer to arbiter",
      "button_title": "Refer this benign flow to the digital-twin arbiter (suspected false negative) — post-decision instruction, off the hot path",
      "err_cap": "manual cap reached",
      "err_off": "arbiter disabled",
      "err_forbidden": "admin role required",
      "err_generic": "referral failed"
    },
    "count_sample": "{{shown}} with alert · among {{total}} loaded flows",
    "no_alert_in_sample": "No alerted flow among the {{n}} loaded (recent) flows — alerted flows may exist earlier in the window.",
    "reset": "Reset filters"
  },
  "ip_detail": {
    "invalid": "“{{value}}” is not a usable IP address.",
    "refresh": "Refresh",
    "loading": "Loading IP profile…",
    "yes": "yes",
    "no": "no",
    "families": "Families",
    "services": "Services",
    "range": {
      "label": "Window",
      "1h": "1 h",
      "6h": "6 h",
      "24h": "24 h"
    },
    "error": {
      "forbidden": "Access denied on anomalies (scope?).",
      "generic": "Failed to fetch anomalies."
    },
    "rep_mode": {
      "active": "applied",
      "shadow": "shadow"
    },
    "role": {
      "source": "sent",
      "destination": "received",
      "both": "loopback"
    },
    "badge": {
      "trusted": "Trusted",
      "trusted_hint": "Permanent whitelist — rule {{match}} · {{reason}}",
      "suppressed": "Suppressed",
      "suppressed_hint": "Active suppression — rule {{match}} · class {{cls}}",
      "policy": "Policy {{action}}",
      "policy_hint": "{{n}} NPI action(s) on this source",
      "none": "No particular status.",
      "probe": "Probe {{hostname}}",
      "probe_hint": "Enrolled eBPF probe whose reported internal IP is this one (probe_certs.ip_address) — certificate status: {{status}}.",
      "partial": "Partial read",
      "partial_hint": "Unavailable sources: {{sources}}. The matching indicators show “unknown”, not “no”."
    },
    "tile": {
      "reputation": "Reputation",
      "reputation_hint": "ADR-0040 source prior read from the decision_context of this source's latest anomaly — that is the reputation AT THAT DECISION, not the live NPI snapshot value (no endpoint exposes it).",
      "reputation_neutral": "neutral",
      "last_activity": "Last activity",
      "first_seen": "First seen",
      "first_seen_hint": "First time Ward³ observed this address in an established outbound flow (ADR-0052). This is NOT \"first appearance on the network\": the registry only knows flows that were published and consumed. A \"≥\" marks a value bounded by the registry's own history start — the address may be older.",
      "first_seen_gap": "Partial history: an offset reset was observed, so part of the history is missing.",
      "first_seen_absent": "no observation",
      "first_seen_absent_partial": "no observation (partial coverage)",
      "first_seen_partial": "Some probes are still warming up and were excluded: coverage is partial.",
      "first_seen_disabled": "registry inactive",
      "anomalies": "Anomalies",
      "anomalies_capped": "This IP hits the 500-event cap: counts fall back to lower bounds.",
      "as_source": "src",
      "as_destination": "dst",
      "max_score": "Max score",
      "flows": "Raw flows",
      "volume": "Volume",
      "peers": "Distinct peers",
      "ports": "ports",
      "trusted": "Trusted IP",
      "suppression": "Suppression",
      "policies": "NPI policies",
      "suppression_ttl": "expires in {{ttl}}",
      "probe": "eBPF probe",
      "probe_hint": "Exact match on `probe_certs.ip_address` (internal IP reported by the probe at enrollment). A revoked or expired probe is still shown, with its status.",
      "last_activity_partial": "Lower bound: one of the sources (anomalies / flows) did not answer — real activity may be more recent.",
      "flows_partial": "Only one flow direction answered (/api/flows filters src_ip or dst_ip, never both): these values are lower bounds, not totals."
    },
    "anomalies": {
      "title": "Anomalies",
      "side_all": "All",
      "side_source": "As source",
      "side_destination": "As destination",
      "count": "{{n}} anomaly(ies)",
      "capped": "{{n}}-event cap reached for this IP — counts are lower bounds",
      "empty": "No anomaly in this window.",
      "open": "Open in Anomalies (XAI detail + actions)",
      "col": {
        "role": "Dir",
        "peer": "Peer",
        "score": "Score",
        "judge": "Judge",
        "family": "Family",
        "confidence": "Confidence",
        "ts": "Timestamp"
      },
      "unavailable": "Anomalies unavailable: reading /api/events failed. The page can neither list them nor claim there are none."
    },
    "flows": {
      "title": "Raw flows",
      "count": "{{n}} flows",
      "empty": "No flow in this window (capture active? scope?).",
      "col": {
        "role": "Dir",
        "flow": "Flow (src → dst)",
        "proto": "Proto",
        "service": "Service",
        "bytes": "Bytes",
        "pkts": "Pkts",
        "start": "Start"
      },
      "open_emitted": "Sent flows",
      "open_emitted_hint": "Open Flows filtered on src_ip = this IP",
      "open_received": "Received flows",
      "open_received_hint": "Open Flows filtered on dst_ip = this IP (the Flows view filters one direction at a time)",
      "unavailable": "Flows unavailable: both directions (sent and received) failed."
    },
    "truncated": "Showing the first {{shown}} rows out of {{total}}.",
    "probe_status": {
      "active": "active",
      "expired": "cert expired",
      "revoked": "revoked"
    },
    "unknown": "unknown",
    "unknown_hint": "Reading this source failed: the page cannot assert absence. Refresh, or check your permissions on the endpoint.",
    "scope": {
      "subtitle": "IP profile unavailable in this scope.",
      "blocked": "A private IP is only unique within a tenant: the same address can designate two machines at two tenants. Your effective scope is “{{scope}}” — pick a tenant in the scope selector to open this profile, otherwise anomalies, flows, probe and policies from several tenants would be merged.",
      "kind": {
        "mssp": "MSSP",
        "platform": "platform",
        "tenant": "tenant"
      }
    },
    "source": {
      "trusted": "trusted sources",
      "suppressions": "suppressions",
      "policies": "policies",
      "probes": "probes",
      "flows": "flows"
    },
    "incidents": {
      "title": "Episodes for this source",
      "count": "{{n}} episode(s)",
      "out_of_window": "outside the page window — 6 h buckets over the full retained history",
      "open_label": "Open in Incidents",
      "open": "Open Incidents filtered on this source (including the “admitted without alert” opt-in)",
      "empty": "No alerting episode for this source.",
      "unavailable": "Episodes unavailable: reading /api/incidents failed. The page can neither list them nor assert there are none.",
      "stages_label": "Stages",
      "no_stages": "no attributed stage",
      "judges_label": "Judges",
      "origins_label": "Origins",
      "empty_hint": "The “admitted without alert” widening (ADR-0053 §B.3-bis) is requested, but a server gate may ignore it without saying so: absence therefore covers alerting episodes, not all episodes.",
      "capped": "{{n}}-episode cap reached — count is a lower bound",
      "admitted_no_alert": "Admitted, no alert",
      "admitted_via_fmt": "Admitted via {{source}}",
      "open_events_label": "Episode events",
      "open_events": "Open Anomalies on this episode's exact window (server-side filter) — one source produces several",
      "count_visible": "{{n}} visible episode(s)",
      "count_visible_hint": "Counts the episodes RENDERED, not every one that exists: the “admitted without alert” widening is requested but a server gate may ignore it without the response saying so. One admitted-without-alert episode in the list would prove the full population is served."
    },
    "campaigns": {
      "title": "Campaigns featuring this IP",
      "count": "{{n}} campaign(s)",
      "role_source": "as source",
      "role_destination": "as destination",
      "dst_label": "Destination",
      "sources_fmt": "{{count}} sources",
      "events_fmt": "{{count}} events",
      "empty": "This IP appears in no campaign cluster.",
      "empty_partial": "No match across the campaigns read — absence is not assertible.",
      "partial_hint": "/api/campaigns has no server-side filter: matching runs over a bounded page, and each campaign also bounds its own source list. A campaign beyond either cap would stay invisible here.",
      "unavailable": "Campaigns unavailable: reading /api/campaigns failed. The page can neither list them nor assert there are none.",
      "partial": "partial coverage"
    }
  },
  "novelty": {
    "title": "Novelty candidates",
    "subtitle": "False-negative review queue (ADR-0053 §F): sources that saw something new and raised no alert. A work proposal, not a detection — the verdict stays human and goes through 4-eyes approval.",
    "filter_source": "Source IP…",
    "detector_any": "Any detector",
    "detector_pair_seen": "New pair",
    "detector_fingerprint": "New fingerprint",
    "clear": "Clear",
    "loading": "Loading candidates…",
    "none": "No candidate in the selected scope. An empty queue is a legitimate state — a fresh pair registry or a probe still warming up produces none.",
    "count_fmt": "{{count}} novel observations",
    "err_load": "Failed to load novelty candidates.",
    "hide_reviewed": "Hide already reviewed",
    "all_reviewed": "Every candidate on this page has already been reviewed.",
    "state_pending": "Awaiting a second analyst",
    "state_labelled": "Labelled",
    "prev": "Previous",
    "next": "Next",
    "page_fmt": "Page {{page}}"
  },
  "incident_label": {
    "propose": "Propose a label",
    "verdict": "Verdict",
    "verdict_attack": "Attack",
    "verdict_benign": "Benign (true negative)",
    "family": "Family",
    "family_pick": "Pick a family…",
    "reason_ph": "Why? (audit trail, 5–2000 chars)",
    "pending_approval": "Proposed — awaiting a second analyst",
    "err_conflict": "Already proposed for this episode, or the episode carries no admission provenance yet.",
    "err_not_found": "Episode not found in this scope (it may have expired — 30-day retention).",
    "err_submit": "Failed to submit the proposal."
  }
}
